import { differenceInMonths, endOfMonth, format, parseISO, startOfDay, startOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { capitalize } from "lodash-es";
import { randomNumberFromInterval } from "./number";

const saoPauloDateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Sao_Paulo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export interface MonthEntries {
  [key: number]: {
    name: string;
    abbreviation: string;
    currentDate: Date;
    start: Date;
    end: Date;
  };
}

export function formatDateInSaoPauloTimeZone(date = new Date()): string {
  const dateParts = saoPauloDateFormatter.formatToParts(date);
  const datePartByType = Object.fromEntries(dateParts.map(({ type, value }) => [type, value]));

  return `${datePartByType.year}-${datePartByType.month}-${datePartByType.day}`;
}

export function formatDate(date: string, formatString: string): string {
  try {
    return format(parseISO(date), formatString, { locale: ptBR });
  } catch {
    return "";
  }
}

export function isoDateToDate(value: string): Date | null {
  try {
    return value ? parseISO(value) : null;
  } catch {
    return null;
  }
}

export function isoDateToStandardDate(value: string): string {
  return formatDate(value, "dd/MM/yyyy");
}

export function isoDateToShortMonthNameShortYear(value: string): string {
  return capitalize(formatDate(value, "LLL/yy"));
}

export function isoDateToDateTime(value: string, separator = " "): string {
  return formatDate(value, `dd/MM/yyyy${separator}HH:mm`);
}

export function isoDateToStandardDateShortYear(value: string): string {
  return formatDate(value, "dd/MM/yy");
}

export function isoDateToShortDateTime(value: string): string {
  return formatDate(value, "dd/MM/yy 'às' HH:mm");
}

export function isoDateToHourAndSecond(value: string): string {
  return formatDate(value, "HH'h'mm");
}

export function isoDateToMonthNameYear(value: string, separator = " 'de' "): string {
  return capitalize(formatDate(value, `MMMM${separator}yyyy`));
}

export function getMonthEntries(now: Date = new Date()) {
  return Array.from({ length: 12 }).reduce<MonthEntries>((data, _, month) => {
    const date = startOfDay(new Date(now.getFullYear(), month, 1));

    return {
      ...data,
      [month]: {
        name: capitalize(format(date, "LLLL", { locale: ptBR })),
        abbreviation: capitalize(format(date, "LLL", { locale: ptBR })),
        currentDate: date,
        start: startOfMonth(date),
        end: endOfMonth(date),
      },
    };
  }, {});
}

export function randomDateBeforeToday(): Date {
  const today = new Date();
  const randomDaysInPast = randomNumberFromInterval(1, 365);
  const pastDate = new Date(today);

  const randomHours = randomNumberFromInterval(0, 23);
  const randomMinutes = randomNumberFromInterval(0, 59);
  const randomSeconds = randomNumberFromInterval(0, 59);

  pastDate.setDate(today.getDate() - randomDaysInPast);
  pastDate.setHours(randomHours, randomMinutes, randomSeconds);
  return pastDate;
}

export const calculateAge = (birthDate?: string): string | null => {
  if (!birthDate) {
    return null;
  }

  const birthDateObj = new Date(birthDate);

  const today = new Date();
  let years = today.getFullYear() - birthDateObj.getFullYear();
  let months = today.getMonth() - birthDateObj.getMonth();

  if (months < 0 || (months === 0 && today.getDate() < birthDateObj.getDate())) {
    years--;
    months = (months + 12) % 12;
  }

  return `${years} ano${years !== 1 ? "s" : ""} e ${months} ${months !== 1 ? "meses" : "mês"}`;
};
export function formatDateToYearsAndMonths(startDate: string, endDate = new Date()): string {
  const totalMonths = differenceInMonths(endDate, startDate);
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years > 0 && months > 0) {
    return `${years} ${years === 1 ? "ano" : "anos"} e ${months} ${months === 1 ? "mês" : "meses"}`;
  } else if (years > 0) {
    return `${years} ${years === 1 ? "ano" : "anos"}`;
  } else {
    return `${months} ${months === 1 ? "mês" : "meses"}`;
  }
}

/**
 * Formata um objeto Date para o padrão dd/MM/yyyy - HHhMM.
 * Ex.: new Date("2025-02-10T12:20:00") → "10/02/2025 - 12h20"
 * @param date - Objeto Date a ser formatado
 */
export function formatDateTimeWithHour(date: Date): string {
  return format(date, "dd/MM/yyyy - HH'h'mm", { locale: ptBR });
}

/**
 * Gera uma data aleatória entre as datas de início e fim fornecidas.
 * @param start - Data de início do intervalo
 * @param end - Data de fim do intervalo
 */
export function getRandomDateFromRange(start: Date, end: Date): Date {
  const startTime = start.getTime();
  const endTime = end.getTime();

  return new Date(startTime + Math.random() * (endTime - startTime));
}

/**
 * Converte uma data no formato civil (YYYY-MM-DD) para um objeto Date.
 * @param value - Data no formato civil (YYYY-MM-DD)
 * @returns Objeto Date correspondente ou null se o valor for inválido
 *
 * @example
 * ```ts
 * const date = civilDateToDate("2023-06-15");
 * console.log(date); // Thu Jun 15 2023 12:00:00 GMT-0300 (Brasilia Standard Time)
 *
 * const invalidDate = civilDateToDate("invalid-date");
 * console.log(invalidDate); // null
 * ```
 */
export const civilDateToDate = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(0);

  date.setHours(12, 0, 0, 0);
  date.setFullYear(year, month - 1, day);

  return date;
};

/**
 * Converte um objeto Date para o formato civil (YYYY-MM-DD).
 * @param date - Objeto Date a ser convertido
 * @returns String representando a data no formato civil (YYYY-MM-DD)
 * @example
 * ```ts
 * const date = new Date(2023, 5, 15); // 15 de junho de 2023
 * const civilDate = dateToCivilDate(date);
 * console.log(civilDate); // "2023-06-15"
 * ```
 */
export const dateToCivilDate = (date: Date): string => {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};
